import { makeStyles, Popover } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import classnames from 'classnames'
import React from 'react'

// Touch screens get taller rows and larger text, as the prototype's menus do.
const TOUCH = '@media (hover: none), (max-width: 500px)'

/** The prototype's dropdown menu (ENG-2080), used by the phone work-order nav bar. */
const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      paper: {
        marginTop: 6,
        maxWidth: 280,
        backgroundColor: neutral.White,
        border: `0.5px solid ${neutral.P85}`,
        borderRadius: 12,
        boxShadow: '0 1px 2px rgba(20,30,50,0.06), 0 6px 16px rgba(20,30,50,0.08), 0 24px 56px rgba(20,30,50,0.16)'
      },
      group: {
        padding: theme.spacing(0, 0.75)
      },
      groupFirst: { paddingTop: 6 },
      groupLast: { paddingBottom: 6 },
      // A divider closes its item's group with a hairline.
      groupDivider: {
        paddingBottom: 6,
        marginBottom: 6,
        borderBottom: `0.5px solid ${neutral.P85}`
      },
      item: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        width: '100%',
        minHeight: 36,
        padding: theme.spacing(1, 1.25),
        boxSizing: 'border-box',
        border: 'none',
        borderRadius: 8,
        background: 'transparent',
        font: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'background-color 0.12s',
        '&:hover': { backgroundColor: '#f0f3f7' },
        [TOUCH]: { minHeight: 44, paddingTop: 11, paddingBottom: 11 }
      },
      itemSelected: { backgroundColor: '#f0f3f7' },
      itemDisabled: {
        cursor: 'default',
        opacity: 0.45,
        '&:hover': { backgroundColor: 'transparent' }
      },
      icon: {
        fontSize: 16,
        width: 16,
        height: 16,
        flexShrink: 0,
        color: neutral.P50
      },
      label: {
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: theme.typography.pxToRem(14),
        color: '#000000',
        [TOUCH]: { fontSize: theme.typography.pxToRem(15) }
      }
    }
  },
  { name: 'WorkOrderNavMenu' }
)

export interface WorkOrderNavMenuItem {
  key: string
  label: string
  icon?: React.ComponentType<{ className?: string }>
  selected?: boolean
  disabled?: boolean
  /** Why a disabled item can't be picked, shown on hover. */
  title?: string
  /** Draw a hairline under this item, closing its group. */
  divider?: boolean
  onSelect: () => void
}

export interface WorkOrderNavMenuProps {
  anchorEl: HTMLElement | null
  onClose: () => void
  items: WorkOrderNavMenuItem[]
  /** Which edge of the trigger the menu lines up with. */
  align: 'left' | 'right'
  minWidth: number
}

/** A list of actions in the prototype's menu style. Picking one closes the menu, then runs it. */
export default function WorkOrderNavMenu({ anchorEl, onClose, items, align, minWidth }: WorkOrderNavMenuProps) {
  const classes = useStyles()
  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: align }}
      transformOrigin={{ vertical: 'top', horizontal: align }}
      classes={{ paper: classes.paper }}
      PaperProps={{ style: { minWidth } }}
    >
      <div role="menu">
        {items.map((item, index) => {
          const { key, label, icon: Icon, selected, disabled, title, divider, onSelect } = item
          return (
            <div
              key={key}
              className={classnames(classes.group, {
                [classes.groupFirst]: index === 0,
                [classes.groupLast]: index === items.length - 1,
                [classes.groupDivider]: divider && index < items.length - 1
              })}
            >
              <button
                type="button"
                role="menuitem"
                aria-disabled={disabled || undefined}
                title={title}
                className={classnames(classes.item, {
                  [classes.itemSelected]: selected,
                  [classes.itemDisabled]: disabled
                })}
                onClick={() => {
                  if (disabled) return
                  onClose()
                  onSelect()
                }}
              >
                {Icon && <Icon className={classes.icon} />}
                <span className={classes.label}>{label}</span>
              </button>
            </div>
          )
        })}
      </div>
    </Popover>
  )
}
